/**
 * Category data for navigation and filtering
 */

interface SubCategory {
  id: string;
  name: string;
  slug: string;
}

interface CategoryData {
  id: string;
  name: string;
  slug: string;
  popularCategories: SubCategory[];
  moreCategories: SubCategory[];
  promo?: {
    title: string;
    subtitle: string;
    ctaText: string;
    ctaLink: string;
    bgColor: string;
    image?: string;
  };
}

// Category data structure matching eBay
export const CATEGORY_DATA: Record<string, CategoryData> = {
  electronics: {
    id: 'electronics',
    name: 'Electronics',
    slug: 'electronics',
    popularCategories: [
      { id: 'smartphones', name: 'Smartphones and accessories', slug: 'smartphones' },
      { id: 'gaming', name: 'Video games and consoles', slug: 'gaming' },
      { id: 'computers', name: 'Computers and tablets', slug: 'computers' },
      { id: 'cameras', name: 'Cameras and photos', slug: 'cameras' },
      { id: 'drones', name: 'Camera drones', slug: 'drones' },
      { id: 'refurbished', name: 'Refurbished', slug: 'refurbished' },
      { id: 'smart-home', name: 'Smart home', slug: 'smart-home' },
    ],
    moreCategories: [
      { id: 'apple', name: 'Apple', slug: 'apple' },
      { id: 'samsung', name: 'Samsung', slug: 'samsung' },
      { id: 'audio', name: 'Portable audio and headphones', slug: 'audio' },
      { id: 'smartwatches', name: 'Smart watches', slug: 'smartwatches' },
      { id: 'deals', name: 'Deals', slug: 'deals' },
    ],
    promo: {
      title: 'Electronics & technology',
      subtitle: 'Check the offers',
      ctaText: 'Shop now',
      ctaLink: '/category/electronics',
      bgColor: 'bg-gradient-to-br from-pink-500 to-rose-600',
    },
  },
  fashion: {
    id: 'fashion',
    name: 'Fashion',
    slug: 'fashion',
    popularCategories: [
      { id: 'mens', name: "Men's clothing", slug: 'mens-clothing' },
      { id: 'womens', name: "Women's clothing", slug: 'womens-clothing' },
      { id: 'shoes', name: 'Shoes', slug: 'shoes' },
      { id: 'bags', name: 'Bags and accessories', slug: 'bags' },
      { id: 'watches', name: 'Watches', slug: 'watches' },
      { id: 'jewelry', name: 'Jewelry', slug: 'jewelry' },
    ],
    moreCategories: [
      { id: 'designer', name: 'Designer brands', slug: 'designer' },
      { id: 'vintage', name: 'Vintage', slug: 'vintage' },
      { id: 'kids', name: "Kids' fashion", slug: 'kids' },
      { id: 'sports', name: 'Sportswear', slug: 'sportswear' },
    ],
    promo: {
      title: 'Fashion finds',
      subtitle: 'Style for less',
      ctaText: 'Shop now',
      ctaLink: '/category/fashion',
      bgColor: 'bg-gradient-to-br from-purple-500 to-indigo-600',
    },
  },
  home: {
    id: 'home',
    name: 'Home & Garden',
    slug: 'home',
    popularCategories: [
      { id: 'furniture', name: 'Furniture', slug: 'furniture' },
      { id: 'kitchen', name: 'Kitchen & dining', slug: 'kitchen' },
      { id: 'bedding', name: 'Bedding', slug: 'bedding' },
      { id: 'garden', name: 'Garden & outdoor', slug: 'garden' },
      { id: 'tools', name: 'Tools', slug: 'tools' },
    ],
    moreCategories: [
      { id: 'decor', name: 'Home decor', slug: 'decor' },
      { id: 'lighting', name: 'Lighting', slug: 'lighting' },
      { id: 'storage', name: 'Storage', slug: 'storage' },
      { id: 'appliances', name: 'Appliances', slug: 'appliances' },
    ],
    promo: {
      title: 'Home essentials',
      subtitle: 'Refresh your space',
      ctaText: 'Shop now',
      ctaLink: '/category/home',
      bgColor: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    },
  },
  collectibles: {
    id: 'collectibles',
    name: 'Collectibles & Art',
    slug: 'collectibles',
    popularCategories: [
      { id: 'trading-cards', name: 'Trading cards', slug: 'trading-cards' },
      { id: 'coins', name: 'Coins & currency', slug: 'coins' },
      { id: 'antiques', name: 'Antiques', slug: 'antiques' },
      { id: 'art', name: 'Art', slug: 'art' },
      { id: 'sports-mem', name: 'Sports memorabilia', slug: 'sports-memorabilia' },
    ],
    moreCategories: [
      { id: 'toys', name: 'Vintage toys', slug: 'vintage-toys' },
      { id: 'comics', name: 'Comic books', slug: 'comics' },
      { id: 'stamps', name: 'Stamps', slug: 'stamps' },
      { id: 'memorabilia', name: 'Entertainment memorabilia', slug: 'memorabilia' },
    ],
    promo: {
      title: 'Rare finds',
      subtitle: 'Unique collectibles',
      ctaText: 'Explore',
      ctaLink: '/category/collectibles',
      bgColor: 'bg-gradient-to-br from-amber-500 to-orange-600',
    },
  },
  motors: {
    id: 'motors',
    name: 'Motors',
    slug: 'motors',
    popularCategories: [
      { id: 'cars', name: 'Cars & trucks', slug: 'cars' },
      { id: 'motorcycles', name: 'Motorcycles', slug: 'motorcycles' },
      { id: 'parts', name: 'Parts & accessories', slug: 'parts' },
      { id: 'tires', name: 'Tires & wheels', slug: 'tires' },
    ],
    moreCategories: [
      { id: 'boats', name: 'Boats', slug: 'boats' },
      { id: 'atvs', name: 'ATVs', slug: 'atvs' },
      { id: 'trailers', name: 'Trailers', slug: 'trailers' },
    ],
    promo: {
      title: 'Auto deals',
      subtitle: 'Parts & vehicles',
      ctaText: 'Shop now',
      ctaLink: '/category/motors',
      bgColor: 'bg-gradient-to-br from-slate-600 to-slate-800',
    },
  },
  sports: {
    id: 'sports',
    name: 'Sports',
    slug: 'sports',
    popularCategories: [
      { id: 'fitness', name: 'Fitness equipment', slug: 'fitness' },
      { id: 'cycling', name: 'Cycling', slug: 'cycling' },
      { id: 'outdoor', name: 'Outdoor sports', slug: 'outdoor' },
      { id: 'team-sports', name: 'Team sports', slug: 'team-sports' },
    ],
    moreCategories: [
      { id: 'golf', name: 'Golf', slug: 'golf' },
      { id: 'fishing', name: 'Fishing', slug: 'fishing' },
      { id: 'camping', name: 'Camping & hiking', slug: 'camping' },
    ],
    promo: {
      title: 'Get active',
      subtitle: 'Sports gear deals',
      ctaText: 'Shop now',
      ctaLink: '/category/sports',
      bgColor: 'bg-gradient-to-br from-blue-500 to-cyan-600',
    },
  },
};

// Navigation categories shown in the top nav
export const NAV_CATEGORIES = [
  { id: 'saved', name: 'Saved', slug: 'saved', hasMegaMenu: false },
  { id: 'electronics', name: 'Electronics', slug: 'electronics', hasMegaMenu: true },
  { id: 'motors', name: 'Motors', slug: 'motors', hasMegaMenu: true },
  { id: 'fashion', name: 'Fashion', slug: 'fashion', hasMegaMenu: true },
  { id: 'collectibles', name: 'Collectibles and Art', slug: 'collectibles', hasMegaMenu: true },
  { id: 'sports', name: 'Sports', slug: 'sports', hasMegaMenu: true },
  { id: 'home', name: 'Home & Garden', slug: 'home', hasMegaMenu: true },
  { id: 'deals', name: 'Deals', slug: 'deals', hasMegaMenu: false },
  { id: 'sell', name: 'Sell', slug: 'sell', hasMegaMenu: false },
];
